import {
    Close,
    Download,
    DrawOutlined,
    Fullscreen,
    FullscreenExit,
    Person,
} from '@mui/icons-material';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Box,
    Typography,
    Button,
    Stack,
    TextField,
    Autocomplete,
    Chip,
    CircularProgress,
    alpha,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import SignatureBoard from './signatureBoard';
import Loader from 'components/v2/common/loader';

import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import { Agent, CompanyAgreementDocumentInterface } from 'types';
import { downloadBlob, getFileBlob } from 'utils/file';

export interface B2BSignPayload {
    party_b_signatory_name: string;
    party_b_signatory_title: string;
    party_b_signature_html: string;
}

interface AgentOption {
    id: number;
    name: string;
    role: string;
    email: string;
}

interface AgreementViewerModalProps {
    title?: string;
    data?: CompanyAgreementDocumentInterface;
    loading: boolean;
    disabled?: boolean;
    showSignButton?: boolean;
    agents?: Agent[];
    currentUserName?: string;
    currentUserRole?: string;
    onClose: () => void;
    onSignAgreement: (payload: B2BSignPayload) => void;
}

const getAgentFullName = (agent: Agent): string =>
    [agent.firstname, agent.middlename, agent.lastname].filter(Boolean).join(' ');

const AgreementViewerModal = ({
    title = 'documentPreview',
    data,
    loading,
    disabled,
    showSignButton = false,
    agents = [],
    currentUserName = '',
    currentUserRole = '',
    onClose,
    onSignAgreement,
}: AgreementViewerModalProps) => {
    const dispatch = useDispatch();
    const { translate } = useTranslate();

    const [fullscreen, setFullscreen] = useState<boolean>(false);
    const [file, setFile] = useState<string>('');
    const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
    const [isLoadingPreview, setIsLoadingPreview] = useState<boolean>(false);
    const [isDownloading, setIsDownloading] = useState<boolean>(false);
    const [openSignatureBoard, setOpenSignatureBoard] = useState<boolean>(false);
    const [signatureHtml, setSignatureHtml] = useState<string>('');

    // Party B signing fields
    const [partyBName, setPartyBName] = useState('');
    const [partyBTitle, setPartyBTitle] = useState('');
    const [selectedAgent, setSelectedAgent] = useState<AgentOption | null>(null);

    const agentOptions: AgentOption[] = useMemo(
        () =>
            agents.map((a) => ({
                id: a.id,
                name: getAgentFullName(a),
                role: a.role || '',
                email: a.email,
            })),
        [agents]
    );

    const hasMultipleAgents = agentOptions.length > 1;

    // Pre-fill name and title from current user on mount
    useEffect(() => {
        if (!showSignButton) return;

        if (hasMultipleAgents) {
            // If multiple agents, try to find current user in the list
            const match = agentOptions.find(
                (a) => a.name.toLowerCase() === currentUserName.toLowerCase()
            );
            if (match) {
                setSelectedAgent(match);
                setPartyBName(match.name);
                setPartyBTitle(match.role);
            }
        } else if (agentOptions.length === 1) {
            // Single agent — auto-fill
            setPartyBName(agentOptions[0].name);
            setPartyBTitle(agentOptions[0].role);
            setSelectedAgent(agentOptions[0]);
        } else if (currentUserName) {
            // No agents loaded — use current user data
            setPartyBName(currentUserName);
            setPartyBTitle(currentUserRole);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showSignButton]);

    useEffect(() => {
        let cancelled = false;
        let objectUrl = '';

        const handlePreview = async () => {
            try {
                if (!data) return;

                setIsLoadingPreview(true);
                setPdfBlob(null);
                if (data.file) {
                    setFile(data.file);
                    return;
                } else if (data.url) {
                    const blob = await getFileBlob(data.url);
                    if (cancelled) return;
                    objectUrl = URL.createObjectURL(blob);
                    setPdfBlob(blob);
                    setFile(objectUrl);
                }
            } catch {
                if (!cancelled) {
                    dispatch(setAlert({ message: 'somethingWentWrong', type: 'error' }));
                }
            } finally {
                if (!cancelled) setIsLoadingPreview(false);
            }
        };

        if (data) {
            void handlePreview();
        }

        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const handleDownload = async () => {
        const sourceUrl = data?.url || data?.file || file;
        if (!pdfBlob && !sourceUrl) {
            dispatch(setAlert({ message: 'somethingWentWrong', type: 'error' }));
            return;
        }

        try {
            let blob = pdfBlob;
            if (!blob && sourceUrl) {
                setIsDownloading(true);
                blob = await getFileBlob(sourceUrl);
                setPdfBlob(blob);
            }
            if (!blob) return;
            downloadBlob(blob, `${translate('agreement')}.pdf`);
        } catch {
            dispatch(setAlert({ message: 'somethingWentWrong', type: 'error' }));
        } finally {
            setIsDownloading(false);
        }
    };

    const handleSignature = (signature: string) => {
        const html = `<img src="${signature}" style="max-width:200px;height:auto;display:block;" alt="${translate('partyBSignature')}" />`;
        setSignatureHtml(html);
        setOpenSignatureBoard(false);
    };

    const handleClearSignature = () => {
        setSignatureHtml('');
        setOpenSignatureBoard(true);
    };

    const handleAgentSelect = (_: unknown, value: AgentOption | null) => {
        setSelectedAgent(value);
        if (value) {
            setPartyBName(value.name);
            setPartyBTitle(value.role);
        } else {
            setPartyBName('');
            setPartyBTitle('');
        }
    };

    const handleSubmitSignature = () => {
        if (!partyBName.trim() || !partyBTitle.trim() || !signatureHtml) {
            dispatch(
                setAlert({
                    message: 'pleaseCompleteSignatureFields',
                    type: 'error',
                })
            );
            return;
        }
        onSignAgreement({
            party_b_signatory_name: partyBName.trim(),
            party_b_signatory_title: partyBTitle.trim(),
            party_b_signature_html: signatureHtml,
        });
    };

    const toggleFullscreen = () => {
        setFullscreen(!fullscreen);
    };

    const canSubmit = partyBName.trim() && partyBTitle.trim() && signatureHtml && !disabled;

    return (
        <Dialog
            open
            maxWidth={fullscreen ? false : 'lg'}
            fullWidth
            fullScreen={fullscreen}
            slotProps={{
                paper: {
                    sx: {
                        height: fullscreen ? '100dvh' : '90dvh',
                        width: fullscreen ? '100vw' : '90vw',
                        maxWidth: fullscreen ? '100vw' : '90vw',
                        m: fullscreen ? 0 : undefined,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    },
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: { xs: 1.5, sm: 0 },
                    pb: { xs: 2, sm: 2 },
                }}
            >
                <Typography
                    variant="h6"
                    component="div"
                    sx={{
                        fontWeight: 600,
                        pr: { xs: 0, sm: 2 },
                        width: { xs: '100%', sm: 'auto' },
                    }}
                >
                    {translate(title)}
                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        gap: 1,
                        flexShrink: 0,
                        width: { xs: '100%', sm: 'auto' },
                        justifyContent: { xs: 'flex-end', sm: 'flex-start' },
                        flexWrap: { xs: 'wrap', sm: 'nowrap' },
                    }}
                >
                    {signatureHtml && showSignButton ? (
                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{ flexWrap: { xs: 'wrap', sm: 'nowrap' } }}
                        >
                            <Button
                                disableRipple
                                color="secondary"
                                variant="outlined"
                                disabled={disabled}
                                onClick={handleClearSignature}
                                size="small"
                                sx={{ minWidth: { xs: 'auto', sm: 100 } }}
                            >
                                {translate('reSign')}
                            </Button>
                            <Button
                                disableRipple
                                color="primary"
                                variant="contained"
                                disabled={!canSubmit}
                                onClick={handleSubmitSignature}
                                size="small"
                                sx={{ minWidth: { xs: 'auto', sm: 120 } }}
                            >
                                {translate('submitSignature')}
                            </Button>
                        </Stack>
                    ) : (
                        showSignButton &&
                        !loading && (
                            <Button
                                variant="contained"
                                color="warning"
                                disableRipple
                                size="small"
                                sx={{
                                    color: '#fff',
                                    '&:hover': { color: '#fff' },
                                    '&.MuiButton-contained': { color: '#fff' },
                                }}
                                onClick={() => setOpenSignatureBoard(true)}
                            >
                                {translate('signAgreement')}
                            </Button>
                        )
                    )}
                    {data?.url && (
                        <IconButton
                            onClick={handleDownload}
                            edge="end"
                            size="small"
                            disabled={isDownloading}
                            aria-label={translate('download')}
                        >
                            {isDownloading ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : (
                                <Download fontSize="small" />
                            )}
                        </IconButton>
                    )}
                    <IconButton
                        onClick={toggleFullscreen}
                        edge="end"
                        size="small"
                        aria-label={translate(fullscreen ? 'exitFullScreen' : 'viewFullScreen')}
                    >
                        {fullscreen ? (
                            <FullscreenExit fontSize="small" />
                        ) : (
                            <Fullscreen fontSize="small" />
                        )}
                    </IconButton>
                    <IconButton
                        onClick={onClose}
                        edge="end"
                        disabled={loading || isLoadingPreview || disabled}
                        size="small"
                        aria-label={translate('close')}
                    >
                        <Close fontSize="small" />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent
                dividers
                sx={{
                    p: 0,
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    overflow: 'hidden',
                    bgcolor: fullscreen ? 'grey.100' : 'background.paper',
                }}
            >
                {loading || isLoadingPreview ? (
                    <Loader />
                ) : (
                    <>
                        {/* PDF preview */}
                        {file && (
                            <Box
                                sx={{
                                    flex: 1,
                                    overflow: 'auto',
                                    minHeight: 0,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    px: fullscreen ? { xs: 1.5, md: 4 } : 0,
                                    py: fullscreen ? { xs: 1.5, md: 3 } : 0,
                                }}
                            >
                                <Box
                                    component="iframe"
                                    src={file}
                                    sx={{
                                        width: '100%',
                                        maxWidth: fullscreen ? 1100 : '100%',
                                        height: '100%',
                                        border: 'none',
                                        bgcolor: '#fff',
                                    }}
                                    title={translate('agreementPdf')}
                                />
                            </Box>
                        )}

                        {/* Signing form */}
                        {showSignButton && !openSignatureBoard && (
                            <Box
                                sx={{
                                    borderTop: '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.02),
                                }}
                            >
                                <Stack spacing={2} sx={{ p: { xs: 2, sm: 2.5 } }}>
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        sx={{ alignItems: 'center' }}
                                    >
                                        <Person sx={{ fontSize: 20, color: 'primary.main' }} />
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                            {translate('Signatory details')}
                                        </Typography>
                                    </Stack>

                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                        {hasMultipleAgents ? (
                                            <Autocomplete
                                                fullWidth
                                                size="small"
                                                options={agentOptions}
                                                value={selectedAgent}
                                                onChange={handleAgentSelect}
                                                getOptionLabel={(opt) => opt.name}
                                                isOptionEqualToValue={(opt, val) =>
                                                    opt.id === val.id
                                                }
                                                renderOption={(props, option) => (
                                                    <Box component="li" {...props} key={option.id}>
                                                        <Stack spacing={0.25}>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{ fontWeight: 500 }}
                                                            >
                                                                {option.name}
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                            >
                                                                {option.role}
                                                            </Typography>
                                                        </Stack>
                                                    </Box>
                                                )}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        required
                                                        label={translate('Select signatory')}
                                                    />
                                                )}
                                            />
                                        ) : (
                                            <TextField
                                                required
                                                label={translate('Full name')}
                                                value={partyBName}
                                                slotProps={{
                                                    input: {
                                                        readOnly: agentOptions.length === 1,
                                                    },
                                                }}
                                                size="small"
                                                fullWidth
                                                sx={{
                                                    ...(agentOptions.length === 1 && {
                                                        '& .MuiInputBase-root': {
                                                            bgcolor: 'action.hover',
                                                        },
                                                    }),
                                                }}
                                            />
                                        )}
                                        <TextField
                                            required
                                            label={translate('Title / Role')}
                                            value={partyBTitle}
                                            onChange={(e) => setPartyBTitle(e.target.value)}
                                            size="small"
                                            fullWidth
                                        />
                                    </Stack>

                                    {/* Signature preview */}
                                    {signatureHtml && (
                                        <Stack
                                            direction="row"
                                            spacing={1.5}
                                            sx={{ alignItems: 'center' }}
                                        >
                                            <Chip
                                                icon={<DrawOutlined />}
                                                label={translate('Signature captured')}
                                                color="success"
                                                size="small"
                                                variant="outlined"
                                            />
                                            <Box
                                                sx={{
                                                    p: 0.75,
                                                    border: '1px solid',
                                                    borderColor: 'success.light',
                                                    borderRadius: 1,
                                                    bgcolor: '#fff',
                                                    maxWidth: 180,
                                                    lineHeight: 0,
                                                }}
                                                dangerouslySetInnerHTML={{ __html: signatureHtml }}
                                            />
                                        </Stack>
                                    )}
                                </Stack>
                            </Box>
                        )}
                    </>
                )}

                {openSignatureBoard && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            backgroundColor: '#ffffff',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            zIndex: 1300,
                        }}
                    >
                        <SignatureBoard
                            onClose={() => setOpenSignatureBoard(false)}
                            onSign={handleSignature}
                        />
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default AgreementViewerModal;
